import { Box, Button, Grid, Typography } from '@mui/material';
import { useGesture } from '@use-gesture/react';
import { fabric } from 'fabric';
import { Canvas, Group, Image, Path } from 'fabric/fabric-impl';
import { Ref, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { canvasJSON } from '../../../../api/whiteboard/drawing/types';
import { fetchCanvasJSON } from '../../../../api/whiteboard/map/site/api';
import { WBMedia } from '../../../../api/whiteboard/map/site/types';
import LabelSelect from './LabelSelect';
import Pencils from './Pencils';
import Tools from './Tools';

const COLORS = ['#DB2B39', '#29335C', '#F3A712', '#441151', '#0D5C63', '#DBF4A7', '#E63B2E', '#FF7733'];
const OBJECTID_KEY = '__objectId';

const FabricCanvas = ({
  json,
  locationId,
  siteId,
  siteName,
  bgImgURL,
  icons,
  container,
  labels,
  collaboratorCount,
  participants,
  socket,
  snapshot,
  queuedOps,
}: {
  json: {
    id: string;
    createdAt: Date;
    hasBeenFinalized: boolean;
    name: string;
    bucketURL: string;
    jsonURL: string;
    height: number;
    width: number;
  }[];
  locationId: string;
  siteId: string;
  siteName: string;
  bgImgURL: string;
  icons: WBMedia[] | undefined;
  container: null;
  labels: { description: string; id: string; name: string }[];
  collaboratorCount: number;
  participants: { socketId: string; userId: string; displayName: string; color: string; joinedAt: number }[];
  socket?: Socket;
  snapshot?: any;
  queuedOps?: any[];
}) => {
  const selectedLabel = useRef('');
  const intentionalRemoveRef = useRef(false);
  const isApplyingRemote = useRef(false);
  const remoteApplyDepth = useRef(0);
  const isInitializingCanvas = useRef(true);
  const canvasInitTokenRef = useRef(0);
  const objectIdCounter = useRef(0);
  const lastAppliedSnapshotRef = useRef('');
  const appliedReplayOpsRef = useRef<Set<string>>(new Set());
  const [canvasReady, setCanvasReady] = useState(false);

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const canvas = useRef<Canvas>();
  const visibleParticipants = participants.slice(0, 4);
  const remainingParticipants = Math.max(0, participants.length - visibleParticipants.length);

  const updateSelectedLabel = (value: string) => {
    selectedLabel.current = value;
  };

  const nextObjectId = () => {
    objectIdCounter.current += 1;
    return `obj_${Date.now()}_${objectIdCounter.current}`;
  };

  const assignObjectIdIfMissing = (obj: any) => {
    if (!obj) return;
    if (!obj[OBJECTID_KEY]) {
      obj[OBJECTID_KEY] = nextObjectId();
    }
  };

  const emitOp = (op: any) => {
    if (!socket) return;
    socket.emit('wb:op', op);
  };

  const beginRemoteApply = () => {
    remoteApplyDepth.current += 1;
    isApplyingRemote.current = true;
  };

  const endRemoteApply = () => {
    remoteApplyDepth.current = Math.max(0, remoteApplyDepth.current - 1);
    if (remoteApplyDepth.current === 0) {
      isApplyingRemote.current = false;
    }
  };

  const getOpReplayKey = (op: any) => {
    const meta = op?._meta || {};
    return [op?.type || '', op?.objectId || '', meta.socketId || '', meta.ts || ''].join('|');
  };

  const shouldApplyReplayOp = (op: any) => {
    const key = getOpReplayKey(op);
    if (!key.trim()) return true;
    if (appliedReplayOpsRef.current.has(key)) return false;
    appliedReplayOpsRef.current.add(key);

    // Keep replay-op dedupe bounded.
    if (appliedReplayOpsRef.current.size > 2000) {
      const iter = appliedReplayOpsRef.current.values();
      const first = iter.next();
      if (!first.done) {
        appliedReplayOpsRef.current.delete(first.value);
      }
    }

    return true;
  };

  const applyRemoteOp = (op: any) => {
    if (!canvas.current || !op || !op.type) return;

    const targetCanvas = canvas.current;
    const targetObj = targetCanvas.getObjects().find((item: any) => item[OBJECTID_KEY] === op.objectId) as any;

    if (op.type === 'object.add' && op.payload?.object) {
      beginRemoteApply();
      const serialized = { ...op.payload.object, [OBJECTID_KEY]: op.objectId };
      fabric.util.enlivenObjects(
        [serialized],
        (objs: any[]) => {
          try {
            objs.forEach((item: any) => {
              if (!canvas.current) return;
              const exists = canvas.current.getObjects().some((obj: any) => obj[OBJECTID_KEY] === op.objectId);
              if (exists) return;
              assignObjectIdIfMissing(item);
              item[OBJECTID_KEY] = op.objectId;
              canvas.current.add(item);
              item.setCoords?.();
            });
            canvas.current?.requestRenderAll();
          } finally {
            endRemoteApply();
          }
        },
        '',
        () => {
          return;
        }
      );
      return;
    }

    beginRemoteApply();
    try {
      if (op.type === 'object.remove' && targetObj) {
        targetCanvas.remove(targetObj);
      } else if (op.type === 'object.update' && targetObj && op.payload) {
        const { left, top, scaleX, scaleY, angle } = op.payload;
        if (typeof left === 'number') targetObj.set({ left });
        if (typeof top === 'number') targetObj.set({ top });
        if (typeof scaleX === 'number') targetObj.set({ scaleX });
        if (typeof scaleY === 'number') targetObj.set({ scaleY });
        if (typeof angle === 'number') targetObj.set({ angle });
        targetObj.setCoords?.();
      }

      targetCanvas.requestRenderAll();
    } finally {
      endRemoteApply();
    }
  };

  /**
   * This funciton will add an icon to the Map.
   * Depending on the Icon clicked will determine the
   * Image for the Icon
   * @param icon: WBMedia: bucketURL will be used for fetching the image
   */
  const handleButtonClick = (icon: WBMedia) => {
    const label = new fabric.Textbox(selectedLabel.current, {
      fontFamily: 'arial',
      left: 5,
      // top: t + (35 + (i * 14)),
      top: 28,
      fontSize: 10,
      width: 40,
      textAlign: 'center',
    });
    // iterate through the words to create a Text for each
    fabric.Image.fromURL(
      icon.bucketURL,
      function (oImg) {
        oImg.scale(0.28);
        const imgWidth = oImg.getScaledWidth();
        const leftOffset = (50 - imgWidth) / 2;
        oImg.left = leftOffset;
        if (canvas.current) {
          const { x, y } = canvas.current.getVpCenter();
          const group = new fabric.Group([label, oImg], {
            left: x,
            top: y,
            width: 50,
            height: 50,
            backgroundColor: 'red',
          });
          // activeObjRef.current = group;
          canvas.current.add(group);
          canvas.current.requestRenderAll();
        }
      },
      { crossOrigin: 'anonymous' }
    );
  };

  /**
   * This function will be based to the child component <Pencils/>
   * it is called whenever a
   */
  const enterDrawMode = (color: string) => {
    // canvas.freeDrawingBrush.width = 3;
    if (canvas.current) {
      canvas.current.isDrawingMode = true;
      // canvas.isDrawingMode = true;
      const brush = canvas.current.freeDrawingBrush;
      brush.color = color;
    }
  };

  const exitDrawMode = () => {
    if (canvas.current) {
      canvas.current.isDrawingMode = false;
    }
  };

  // this function is for adjusting the pen drawing size
  const adjustPenSize = (size: number) => {
    if (canvas.current) {
      canvas.current.freeDrawingBrush.width = size;
    }
  };

  // use-gesture/react
  const bind = useGesture(
    {
      // eslint-disable-next-line
      onDrag: ({ canceled, movement, touches }: any) => {
        if (canceled) {
          return;
        }
        if (touches == 1) {
          if (canvas.current?.getZoom() == 1) return;
          if (canvas.current?.isDrawingMode) return;
          if (canvas.current?.getActiveObject()) return;
          lockAllCanvasObjects();
          if (canvas.current) {
            canvas.current.selection = false;
          }
          // negative -> right
          // positive -> left
          const delta = new fabric.Point(0.09 * movement[0], 0.09 * movement[1]);
          // canvas.current?.relativePan(delta);
          canvas.current?.relativePan(delta);
        }
      },
      onDragEnd: () => {
        unlockAllCanvasObjects();
        if (canvas.current) {
          canvas.current.selection = true;
        }
      },
      // eslint-disable-next-line
      onPinch: ({ canceled, origin: [ox, oy], movement: [ms, m2], memo }: any) => {
        if (canceled) return;
        lockAllCanvasObjects();
        if (canvas.current) {
          canvas.current.selection = false;
        }
        if (memo) {
          let zoom = memo * ms;
          if (zoom > 8) zoom = 8;
          if (zoom < 1) {
            zoom = 1;
            canvas.current?.setViewportTransform([1, 0, 0, 1, 0, 0]);
          } else {
            canvas.current?.zoomToPoint({ x: ox, y: oy }, zoom);
          }
        } else {
          memo = canvas.current?.getZoom();
        }
        return memo;
      },
    },
    {
      pinch: { threshold: 0.1 },
      eventOptions: { passive: false },
      drag: {
        // bounds: {top: (-1 * windowHeight), bottom: windowHeight, left: (-1 * windowWidth), right: windowWidth},
        filterTaps: true,
        // preventDefault: true,
        pointer: {
          touch: true,
        },
      },
    }
  );
  // Called for drag of pinch zoom to lock down all objects
  const lockAllCanvasObjects = () => {
    // const objects = canvas.getObjects();
    if (canvas.current) {
      // eslint-disable-next-line
      const objects: any = canvas.current.getObjects();
      // eslint-disable-next-line
      objects?.forEach((obj: any) => {
        // obj.selectable = false;
        obj.hasControls = false;
        obj.hasBorders = false;
        obj.lockMovementX = true;
        obj.lockMovementY = true;
      });
    }
  };
  // Called for END of drag of pinch zoom to unlock all objects
  const unlockAllCanvasObjects = () => {
    const objects = canvas.current?.getObjects();
    // eslint-disable-next-line
    objects?.forEach((obj: any) => {
      // obj.selectable = true;
      obj.hasControls = true;
      obj.hasBorders = true;
      obj.lockMovementX = false;
      obj.lockMovementY = false;
    });
  };
  // DELETE a single Object
  const runWithIntentionalRemove = (callback: () => void) => {
    if (intentionalRemoveRef.current) return;
    intentionalRemoveRef.current = true;
    callback();
    window.setTimeout(() => {
      intentionalRemoveRef.current = false;
    }, 0);
  };

  const handleObjectDelete = () => {
    const activeObject = canvas.current?.getActiveObject();
    if (activeObject) {
      runWithIntentionalRemove(() => {
        canvas.current?.remove(activeObject);
      });
    }
  };
  // RESET Canvas
  const handleClearCanvas = () => {
    if (canvas.current) {
      runWithIntentionalRemove(() => {
        canvas.current?.remove(...canvas.current.getObjects());
      });
    }
  };

  const canvasResetView = () => {
    canvas.current?.setViewportTransform([1, 0, 0, 1, 0, 0]);
  };

  const useFabric = (ref: Ref<Canvas | unknown> | undefined) => {
    const canvas = useRef<Canvas>();

    useImperativeHandle(ref, () => {
      return canvas.current;
    });

    const fabricRef = useCallback((element: HTMLCanvasElement | null) => {
      if (!element) {
        canvasInitTokenRef.current += 1;
        setCanvasReady(false);
        isInitializingCanvas.current = false;
        return canvas.current?.dispose();
      }
      const initToken = canvasInitTokenRef.current + 1;
      canvasInitTokenRef.current = initToken;
      const activeCanvas = new fabric.Canvas(element, { backgroundColor: '#eeeeee' });
      canvas.current = activeCanvas;

      const isCanvasUsable = () => {
        return (
          canvasInitTokenRef.current === initToken &&
          canvas.current === activeCanvas &&
          !!(activeCanvas as any)?.contextContainer
        );
      };

      setCanvasReady(true);
      isInitializingCanvas.current = true;
      let containerHeight = 0;
      let containerWidth = 0;
      // check to see if we have the container clientHeight and clientWidth exist
      // If we DO NOT have the container ref then we will use the entire window width and height
      // and adjust those by width * .9 and height * .8
      if (container) {
        // if it does then we have the width and height of the parent container, so
        // we can keep the width but need to adjust the height by *.8
        containerHeight = (container as HTMLElement).clientHeight * 0.8;
        containerWidth = (container as HTMLElement).clientWidth;
      } else {
        containerHeight = window.screen.height * 0.8;
        containerWidth = window.screen.width * 0.9;
      }
      activeCanvas.setWidth(containerWidth);
      activeCanvas.setHeight(containerHeight);
      // box that will overlay the canvas
      const loadingJSONBox = new fabric.Rect({
        height: containerHeight,
        width: containerWidth,
        fill: '#ffffff', // pink
      });
      // text that will say loading objects
      const loadingJSONTextObj = new fabric.Text('Loading...', {
        fontSize: 24,
        left: containerWidth / 2 - 100,
        top: containerHeight / 2,
      });
      // grouping both objects to add to canvas
      const group = new fabric.Group([loadingJSONBox, loadingJSONTextObj], {
        left: 0,
        top: 0,
        height: containerHeight,
        width: containerWidth,
        selectable: false,
      });
      activeCanvas.add(group);
      activeCanvas.renderAll();
      fabric.Image.fromURL(
        bgImgURL,
        async function (img) {
          if (!isCanvasUsable()) return;

          const width = img.width ? img.width : 400;
          const height = img.height ? img.height : 600;

          const scalex = containerWidth / width;
          const scaley = containerHeight / height;
          // setting the bounds
          activeCanvas.setBackgroundImage(
            img,
            () => {
              if (!isCanvasUsable()) return;
              activeCanvas.renderAll();
            },
            {
            scaleX: scalex,
            scaleY: scaley,
            }
          );
          if (json.length > 0) {
            // How do we get the new screen size of the canvas?
            // newWidth / originalWidth
            const widthScale = containerWidth / json[0].width;
            const heightScale = containerHeight / json[0].height;
            const res: canvasJSON | null = await fetchCanvasJSON(json[0].jsonURL);
            if (!isCanvasUsable()) return;
            if (res) {
              // now time to add all the objects
              fabric.util.enlivenObjects(
                res.objects,
                (objs: (Path | Group | Image)[]) => {
                  if (!isCanvasUsable()) return;
                  objs.forEach((item: Path | Group | Image) => {
                    assignObjectIdIfMissing(item as any);
                    activeCanvas.add(item);
                    item.left = item.left ? item.left * widthScale : 0;
                    item.top = item.top ? item.top * heightScale : 0;

                    item.scaleX = item.scaleX ? item.scaleX * widthScale : 0;
                    item.scaleY = heightScale;
                    item.setCoords();
                  });
                  // canvas.current?.renderAll();
                },
                '',
                () => {
                  return;
                }
              );
              // now we remove the group object with the loading message
              if (!isCanvasUsable()) return;
              activeCanvas.remove(group);
              activeCanvas.requestRenderAll();
              isInitializingCanvas.current = false;
            } else {
              toast.error('Failed to load previous JSON. Refresh to ensure no overwrite occurs.');
              isInitializingCanvas.current = false;
            }
          } else {
            if (!isCanvasUsable()) return;
            activeCanvas.remove(group);
            activeCanvas.requestRenderAll();
            isInitializingCanvas.current = false;
          }
        },
        { crossOrigin: 'anonymous' }
      );
    }, []);
    return fabricRef;
  };

  /* eslint-disable react/display-name */
  const MyFabric = forwardRef((props, ref: Ref<Canvas | unknown> | undefined) => {
    const fabricRef = useFabric(ref);
    return <canvas ref={fabricRef} />;
  });

  useEffect(() => {
    const handleCanvasDeleteShortcut = (event: KeyboardEvent) => {
      if (!canvas.current) return;

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isTypingField = tagName === 'input' || tagName === 'textarea' || target?.isContentEditable;
      if (isTypingField) return;

      if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        event.preventDefault();
        canvasResetView();
        return;
      }

      const activeObject = canvas.current.getActiveObject();

      if (activeObject && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        const distance = event.shiftKey ? 8 : 2;
        if (event.key === 'ArrowUp') activeObject.top = (activeObject.top || 0) - distance;
        if (event.key === 'ArrowDown') activeObject.top = (activeObject.top || 0) + distance;
        if (event.key === 'ArrowLeft') activeObject.left = (activeObject.left || 0) - distance;
        if (event.key === 'ArrowRight') activeObject.left = (activeObject.left || 0) + distance;
        activeObject.setCoords();
        canvas.current.requestRenderAll();

        const movableObj = activeObject as any;
        if (movableObj[OBJECTID_KEY]) {
          emitOp({
            type: 'object.update',
            objectId: movableObj[OBJECTID_KEY],
            payload: {
              left: movableObj.left,
              top: movableObj.top,
              scaleX: movableObj.scaleX,
              scaleY: movableObj.scaleY,
              angle: movableObj.angle,
            },
          });
        }
        return;
      }

      if (event.key !== 'Delete' && event.key !== 'Backspace') return;

      if (!activeObject) return;

      event.preventDefault();
      runWithIntentionalRemove(() => {
        canvas.current?.remove(activeObject);
      });
      canvas.current.requestRenderAll();
    };

    window.addEventListener('keydown', handleCanvasDeleteShortcut);
    return () => {
      window.removeEventListener('keydown', handleCanvasDeleteShortcut);
    };
  }, []);

  useEffect(() => {
    if (!socket || !canvasReady || !canvas.current) return;

    const targetCanvas = canvas.current;

    const onObjectAdded = (evt: any) => {
      if (isApplyingRemote.current || isInitializingCanvas.current) return;
      const obj = evt?.target as any;
      if (!obj) return;
      assignObjectIdIfMissing(obj);
      emitOp({
        type: 'object.add',
        objectId: obj[OBJECTID_KEY],
        payload: {
          object: obj.toObject([OBJECTID_KEY]),
          type: obj.type,
        },
      });
    };

    const onObjectModified = (evt: any) => {
      if (isApplyingRemote.current || isInitializingCanvas.current) return;
      const obj = evt?.target as any;
      if (!obj) return;
      assignObjectIdIfMissing(obj);
      emitOp({
        type: 'object.update',
        objectId: obj[OBJECTID_KEY],
        payload: {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
        },
      });
    };

    const onObjectRemoved = (evt: any) => {
      if (isApplyingRemote.current || isInitializingCanvas.current || !intentionalRemoveRef.current) return;
      const obj = evt?.target as any;
      if (!obj) return;
      assignObjectIdIfMissing(obj);
      emitOp({
        type: 'object.remove',
        objectId: obj[OBJECTID_KEY],
      });
    };

    const onRemoteOp = (op: any) => {
      if (!shouldApplyReplayOp(op)) return;
      applyRemoteOp(op);
    };

    targetCanvas.on('object:added', onObjectAdded);
    targetCanvas.on('object:modified', onObjectModified);
    targetCanvas.on('object:removed', onObjectRemoved);
    socket.on('wb:op', onRemoteOp);

    return () => {
      targetCanvas.off('object:added', onObjectAdded);
      targetCanvas.off('object:modified', onObjectModified);
      targetCanvas.off('object:removed', onObjectRemoved);
      socket.off('wb:op', onRemoteOp);
    };
  }, [socket, canvasReady]);

  useEffect(() => {
    if (!canvasReady || !canvas.current) return;

    const snapshotString = snapshot ? JSON.stringify(snapshot) : '';
    if (snapshotString && snapshotString === lastAppliedSnapshotRef.current) return;

    const targetCanvas = canvas.current;

    if (snapshotString) {
      appliedReplayOpsRef.current.clear();
      isApplyingRemote.current = true;
      targetCanvas.loadFromJSON(snapshot, () => {
        targetCanvas.getObjects().forEach((item: any) => assignObjectIdIfMissing(item));
        targetCanvas.requestRenderAll();
        isApplyingRemote.current = false;

        if (Array.isArray(queuedOps)) {
          queuedOps.forEach((op) => {
            if (!shouldApplyReplayOp(op)) return;
            applyRemoteOp(op);
          });
        }
      });
      lastAppliedSnapshotRef.current = snapshotString;
      return;
    }

    if (Array.isArray(queuedOps) && queuedOps.length > 0) {
      queuedOps.forEach((op) => {
        if (!shouldApplyReplayOp(op)) return;
        applyRemoteOp(op);
      });
    }
  }, [snapshot, queuedOps, canvasReady]);

  useEffect(() => {
    if (!socket || !canvasReady || !canvas.current) return;

    const interval = window.setInterval(() => {
      if (!canvas.current || isApplyingRemote.current) return;
      socket.emit('wb:snapshot', {
        snapshot: canvas.current.toDatalessJSON([OBJECTID_KEY]),
      });
    }, 5000);

    return () => {
      window.clearInterval(interval);
    };
  }, [socket, canvasReady]);

  return (
    <Box position={'relative'} display={'flex'} flexDirection={'column'} flexWrap={'wrap'} bgcolor={'#ffffff'}>
      <Grid
        container
        minHeight={'76px'}
        alignItems={'center'}
        bgcolor={'#ffffff'}
        borderBottom={'1px solid #d7dde7'}
        px={1}
      >
        <Grid item xs={12} md={4}>
          <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} px={1.5} py={1}>
            <Typography fontSize={22} fontWeight={700} color={'#0f172a'}>
              {siteName}
            </Typography>
            <Typography fontSize={12} color={'#64748b'}>
              Edit icons, labels, and annotations live.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box display={'flex'} alignItems={'center'} justifyContent={{ xs: 'space-between', md: 'flex-end' }} gap={1} flexWrap={'wrap'}>
            <Tools
              locationId={locationId}
              siteId={siteId}
              deleteObj={handleObjectDelete}
              refresh={handleClearCanvas}
              resetView={canvasResetView}
              canvas={canvas}
              lockAllItems={lockAllCanvasObjects}
            />
            <Pencils colors={COLORS} drawMode={enterDrawMode} endDrawMode={exitDrawMode} setPenSize={adjustPenSize} />
          </Box>
        </Grid>
      </Grid>
      <Box
        width={'100%'}
        height={'70vh'}
        ref={canvasContainerRef}
        {...bind()}
        display={'grid'}
        sx={{ placeItems: 'center' }}
      >
        {/* <canvas ref={canvasRef}/> */}
        <MyFabric ref={canvas} />
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-end'}
          gap={0.75}
          position={'absolute'}
          top={8}
          right={8}
          zIndex={100}
          sx={{ pointerEvents: 'none' }}
        >
          <Box px={1} py={0.5} borderRadius={1} bgcolor={'rgba(27, 63, 130, 0.88)'}>
            <Typography fontSize={12} color={'#fff'}>
              Live editors: {collaboratorCount}
            </Typography>
          </Box>
          {participants.length > 0 && (
            <Box
              display={'flex'}
              flexWrap={'wrap'}
              justifyContent={'flex-end'}
              gap={0.5}
              maxWidth={360}
              px={1}
              py={0.75}
              borderRadius={1}
              bgcolor={'rgba(15, 23, 42, 0.72)'}
            >
              {visibleParticipants.map((participant) => (
                <Box
                  key={participant.socketId}
                  display={'flex'}
                  alignItems={'center'}
                  gap={0.75}
                  px={1}
                  py={0.4}
                  borderRadius={999}
                  border={`1px solid ${participant.color}`}
                  bgcolor={'rgba(255,255,255,0.06)'}
                >
                  <Box width={8} height={8} borderRadius={'50%'} bgcolor={participant.color} />
                  <Typography fontSize={11} color={'#fff'} noWrap>
                    {participant.displayName}
                  </Typography>
                </Box>
              ))}
              {remainingParticipants > 0 && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  px={1}
                  py={0.4}
                  borderRadius={999}
                  border={'1px solid rgba(255,255,255,0.24)'}
                  bgcolor={'rgba(255,255,255,0.06)'}
                >
                  <Typography fontSize={11} color={'#fff'}>
                    +{remainingParticipants} more
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box
          position={'absolute'}
          bottom={8}
          left={8}
          zIndex={100}
          px={1}
          py={0.5}
          borderRadius={1}
          bgcolor={'rgba(0, 0, 0, 0.66)'}
        >
          <Typography fontSize={11} color={'#fff'}>
            Shortcuts: Del remove, Shift+Arrows nudge, Ctrl/Cmd+0 reset view
          </Typography>
        </Box>
      </Box>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={1.25}
        width={'100%'}
        minHeight={'92px'}
        bgcolor={'#ffffff'}
        borderTop={'1px solid #d7dde7'}
        px={1.5}
        py={1}
      >
        <Box width={{ xs: '100%', md: 300 }} minWidth={{ xs: '100%', md: 300 }} flexShrink={0}>
          <LabelSelect labels={labels} updateParent={updateSelectedLabel} />
        </Box>
        <Box
          display={'flex'}
          alignItems={'center'}
          gap={0.75}
          flex={1}
          width={'100%'}
          minWidth={0}
          pb={{ xs: 0.25, md: 0 }}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {icons?.map((icon) => {
            return (
              <Button
                key={icon.id}
                onClick={() => handleButtonClick(icon)}
                sx={{
                  minWidth: 58,
                  width: 58,
                  height: 58,
                  padding: 0.75,
                  borderRadius: 2,
                  border: '1px solid #d7dde7',
                  bgcolor: '#f8fafc',
                  '&:hover': { bgcolor: '#eef2f7' },
                }}
              >
                <Box component={'img'} src={icon.bucketURL} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FabricCanvas;
